import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { OrgType, OrgStatus } from '@tips-sso/shared';
import { PageHeader } from '../components/PageHeader.js';
import { DataTable, type Column } from '../components/DataTable.js';
import { Modal } from '../components/Modal.js';
import { Field, StatusBadge } from '../components/Field.js';
import { errorMessage } from '../api/client.js';
import { createOrg, fetchOrgs, type CreateOrgInput } from '../api/endpoints.js';
import type { Organization } from '@tips-sso/shared';

const ORG_TYPES: OrgType[] = [
  'tips_internal',
  'tips_product_ops',
  'direct_customer',
  'reseller',
  'reseller_customer',
  'demo_or_trial',
];
const ORG_STATUSES: OrgStatus[] = ['active', 'trial', 'suspended', 'terminated'];

export function OrgsPage() {
  const navigate = useNavigate();
  const qc = useQueryClient();
  const [orgType, setOrgType] = useState<OrgType | ''>('');
  const [status, setStatus] = useState<OrgStatus | ''>('');
  const [open, setOpen] = useState(false);

  const orgs = useQuery({
    queryKey: ['orgs', orgType, status],
    queryFn: () =>
      fetchOrgs({
        orgType: orgType || undefined,
        status: status || undefined,
      }),
  });

  const columns: Column<Organization>[] = [
    { key: 'displayName', header: 'Name', render: (o) => o.displayName },
    { key: 'orgType', header: 'Type', render: (o) => <span className="badge">{o.orgType}</span> },
    { key: 'domain', header: 'Primary Domain', render: (o) => o.primaryDomain ?? '—' },
    { key: 'status', header: 'Status', render: (o) => <StatusBadge status={o.status} /> },
    {
      key: 'created',
      header: 'Created',
      render: (o) => new Date(o.createdAt).toLocaleDateString(),
    },
  ];

  return (
    <div className="page">
      <PageHeader
        title="Organizations"
        subtitle="All TIPSSSO organizations across direct, reseller, and internal tenants."
        actions={
          <button type="button" className="btn btn--primary" onClick={() => setOpen(true)}>
            New Organization
          </button>
        }
      />

      <div className="filter-row">
        <Field label="Org type">
          <select
            className="field__select"
            value={orgType}
            onChange={(e) => setOrgType(e.target.value as OrgType | '')}
          >
            <option value="">All types</option>
            {ORG_TYPES.map((t) => (
              <option key={t} value={t}>
                {t}
              </option>
            ))}
          </select>
        </Field>
        <Field label="Status">
          <select
            className="field__select"
            value={status}
            onChange={(e) => setStatus(e.target.value as OrgStatus | '')}
          >
            <option value="">All statuses</option>
            {ORG_STATUSES.map((s) => (
              <option key={s} value={s}>
                {s}
              </option>
            ))}
          </select>
        </Field>
      </div>

      <DataTable
        columns={columns}
        rows={orgs.data}
        rowKey={(o) => o.id}
        isLoading={orgs.isLoading}
        isError={orgs.isError}
        error={orgs.error}
        onRetry={() => void orgs.refetch()}
        onRowClick={(o) => navigate(`/tipsconnect/admin/orgs/${o.id}`)}
        emptyMessage="No organizations match these filters."
      />

      <CreateOrgModal
        open={open}
        onClose={() => setOpen(false)}
        onCreated={() => {
          setOpen(false);
          void qc.invalidateQueries({ queryKey: ['orgs'] });
        }}
      />
    </div>
  );
}

function CreateOrgModal({
  open,
  onClose,
  onCreated,
}: {
  open: boolean;
  onClose: () => void;
  onCreated: () => void;
}) {
  const [form, setForm] = useState<CreateOrgInput>({
    legalName: '',
    displayName: '',
    orgType: 'direct_customer',
  });

  const mutation = useMutation({
    mutationFn: () => createOrg(form),
    onSuccess: onCreated,
  });

  return (
    <Modal open={open} title="New Organization" onClose={onClose}>
      <form
        className="form"
        onSubmit={(e) => {
          e.preventDefault();
          mutation.mutate();
        }}
      >
        {mutation.isError && <div className="form-error">{errorMessage(mutation.error)}</div>}
        <Field label="Legal name">
          <input
            className="field__input"
            value={form.legalName}
            required
            onChange={(e) => setForm({ ...form, legalName: e.target.value })}
          />
        </Field>
        <Field label="Display name">
          <input
            className="field__input"
            value={form.displayName}
            required
            onChange={(e) => setForm({ ...form, displayName: e.target.value })}
          />
        </Field>
        <Field label="Org type">
          <select
            className="field__select"
            value={form.orgType}
            onChange={(e) => setForm({ ...form, orgType: e.target.value as OrgType })}
          >
            {ORG_TYPES.map((t) => (
              <option key={t} value={t}>
                {t}
              </option>
            ))}
          </select>
        </Field>
        <Field label="Primary domain" hint="Optional">
          <input
            className="field__input"
            value={form.primaryDomain ?? ''}
            onChange={(e) => setForm({ ...form, primaryDomain: e.target.value || undefined })}
          />
        </Field>
        <div className="form__actions">
          <button type="button" className="btn btn--ghost" onClick={onClose}>
            Cancel
          </button>
          <button type="submit" className="btn btn--primary" disabled={mutation.isPending}>
            {mutation.isPending ? 'Creating…' : 'Create'}
          </button>
        </div>
      </form>
    </Modal>
  );
}
